import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function FonctionnementPage() {
  const steps = [
    {
      number: 1,
      title: 'Choisir votre création',
      description: 'Parcourez nos catégories et sélectionnez le type de création qui correspond à vos besoins : livre personnalisé, personnage animé, invitation ou portrait illustré.',
      icon: 'ri-cursor-line',
      color: 'from-primary-blue to-purple-600',
      image: 'https://public.readdy.ai/ai/img_res/af1b96e6284993f192fdc9c3d2ca29d6.jpg',
    },
    {
      number: 2,
      title: 'Personnaliser en quelques clics',
      description: 'Répondez à un questionnaire simple et guidé. Ajoutez les informations personnelles : nom, âge, apparence, préférences. Notre mascotte Pixel vous accompagne à chaque étape.',
      icon: 'ri-edit-line',
      color: 'from-primary-mint to-green-600',
      image: 'https://readdy.ai/api/search-image?query=A%20friendly%20Pixel%20mascot%20character%20helping%20a%20person%20fill%20out%20a%20personalization%20form%20on%20a%20computer%2C%20colorful%20interface%20elements%2C%20Pixar%20Disney%20style%2C%20simple%20clean%20background%2C%20helpful%20and%20encouraging%20atmosphere&width=600&height=400&seq=step2&orientation=landscape',
    },
    {
      number: 3,
      title: 'Payer en toute sécurité',
      description: 'Effectuez votre paiement via notre système sécurisé Stripe. Pas d\'abonnement, pas de frais cachés. Vous ne payez que pour ce que vous commandez.',
      icon: 'ri-secure-payment-line',
      color: 'from-primary-yellow to-orange-500',
      image: 'https://readdy.ai/api/search-image?query=A%20secure%20payment%20interface%20with%20credit%20card%20and%20lock%20icons%20showing%20safe%20transaction%20in%20Pixar%20Disney%20style%2C%20professional%20design%2C%20simple%20clean%20background%2C%20trustworthy%20atmosphere%2C%20modern%20payment%20system&width=600&height=400&seq=step3&orientation=landscape',
    },
    {
      number: 4,
      title: 'Recevoir votre création',
      description: 'Recevez votre création numérique personnalisée en moins de 24h directement dans votre boîte email. Formats PDF, JPG ou MP4 selon votre choix, prêts à partager ou imprimer.',
      icon: 'ri-mail-send-line',
      color: 'from-primary-coral to-pink-600',
      image: 'https://readdy.ai/api/search-image?query=A%20happy%20person%20receiving%20an%20email%20notification%20with%20a%20beautiful%20digital%20creation%20on%20their%20phone%2C%20magical%20sparkles%2C%20Pixar%20Disney%20style%2C%20simple%20clean%20background%2C%20joyful%20moment%2C%20celebration%20atmosphere&width=600&height=400&seq=step4&orientation=landscape',
    },
  ];

  const features = [
    {
      icon: 'ri-time-line',
      title: 'Livraison Express',
      description: 'Moins de 24h',
    },
    {
      icon: 'ri-refresh-line',
      title: 'Modifications Gratuites',
      description: 'Jusqu\'à satisfaction',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Paiement Sécurisé',
      description: '100% protégé',
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Support Dédié',
      description: 'Réponse rapide',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-6xl font-fredoka font-bold text-gray-900 mb-6">
              Comment ça{' '}
              <span className="bg-gradient-to-r from-primary-blue via-purple-600 to-primary-coral bg-clip-text text-transparent">
                fonctionne ?
              </span>
            </h1>
            <p className="text-xl font-quicksand text-gray-600 max-w-3xl mx-auto">
              Un processus simple et guidé pour créer vos souvenirs magiques en quelques minutes
            </p>
          </motion.div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className={`mb-32 last:mb-0 ${
                index % 2 === 0 ? '' : 'flex-row-reverse'
              }`}
            >
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 0 ? '' : 'lg:flex-row-reverse'
              }`}>
                {/* Content */}
                <div className={index % 2 === 0 ? '' : 'lg:order-2'}>
                  {/* Step Number Badge */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-20 h-20 flex items-center justify-center bg-gradient-to-br ${step.color} rounded-3xl shadow-soft`}>
                      <span className="text-3xl font-fredoka font-bold text-white">
                        {step.number}
                      </span>
                    </div>
                    <div className={`w-16 h-16 flex items-center justify-center bg-gradient-to-br ${step.color} rounded-2xl`}>
                      <i className={`${step.icon} text-3xl text-white`}></i>
                    </div>
                  </div>

                  <h2 className="text-4xl font-fredoka font-bold text-gray-900 mb-6">
                    {step.title}
                  </h2>
                  <p className="text-lg font-quicksand text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Image */}
                <div className={index % 2 === 0 ? '' : 'lg:order-1'}>
                  <div className="relative rounded-4xl overflow-hidden shadow-soft-lg">
                    <div className="w-full h-96">
                      <img
                        src={step.image}
                        alt={step.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Pixel Guide Section */}
      <section className="py-24 bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Pixel Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-full h-[500px] flex items-center justify-center">
                <img
                  src="https://readdy.ai/api/search-image?query=The%20friendly%20Pixel%20mascot%20character%20in%20a%20helpful%20guide%20pose%20holding%20a%20magic%20wand%20with%20sparkles%2C%20Pixar%20Disney%20animation%20style%2C%20cheerful%20expression%2C%20colorful%20outfit%2C%20simple%20gradient%20background%2C%20professional%203D%20rendering%2C%20welcoming%20atmosphere&width=600&height=800&seq=pixelguide1&orientation=portrait"
                  alt="Pixel votre guide"
                  className="w-full h-full object-contain drop-shadow-2xl"
                />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 rounded-full mb-6">
                <span className="text-2xl">✨</span>
                <span className="font-quicksand font-semibold text-gray-800">
                  Votre guide personnel
                </span>
              </div>

              <h2 className="text-4xl lg:text-5xl font-fredoka font-bold text-gray-900 mb-6 leading-tight">
                Pixel vous accompagne à chaque étape
              </h2>

              <p className="text-lg font-quicksand text-gray-600 leading-relaxed mb-8">
                Notre mascotte Pixel est là pour vous guider tout au long du processus de création. Avec des conseils personnalisés et une interface intuitive, créer votre magie n'a jamais été aussi simple !
              </p>

              <div className="space-y-4">
                {[
                  'Interface simple et intuitive',
                  'Conseils personnalisés à chaque étape',
                  'Prévisualisation en temps réel',
                  'Assistance disponible 7j/7',
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-primary-mint rounded-full flex-shrink-0">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="font-quicksand text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-fredoka font-bold text-gray-900 mb-4">
              Nos Garanties
            </h2>
            <p className="text-lg font-quicksand text-gray-600">
              Votre satisfaction est notre priorité
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-3xl p-8 shadow-soft hover:shadow-soft-lg transition-all duration-300 text-center border border-gray-100"
              >
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-gradient-to-br from-primary-blue to-purple-600 rounded-2xl">
                  <i className={`${feature.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-fredoka font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="font-quicksand text-gray-600">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-24 bg-neutral-lighter">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-4xl font-fredoka font-bold text-gray-900 mb-6">
              Des questions ?
            </h2>
            <p className="text-lg font-quicksand text-gray-600 mb-8">
              Consultez notre FAQ pour plus d'informations ou contactez-nous directement
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/faq"
                className="px-8 py-4 bg-gray-900 text-white font-quicksand font-bold rounded-full hover:shadow-soft-lg transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
              >
                <i className="ri-question-line"></i>
                <span>Voir la FAQ</span>
              </a>
              <a
                href="/contact"
                className="px-8 py-4 bg-white border-2 border-gray-900 text-gray-900 font-quicksand font-bold rounded-full hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
              >
                <i className="ri-customer-service-line"></i>
                <span>Nous contacter</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-blue via-purple-600 to-primary-coral"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-5xl lg:text-6xl font-fredoka font-bold text-white mb-6 leading-tight">
              Prêt à commencer votre aventure ?
            </h2>
            <p className="text-xl font-quicksand text-white/90 mb-10">
              Créez votre première création magique en quelques minutes
            </p>
            <a
              href="/creations"
              className="inline-flex items-center gap-3 px-10 py-5 bg-white text-gray-900 font-quicksand font-bold text-lg rounded-full hover:shadow-soft-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <span>Découvrir nos créations</span>
              <i className="ri-arrow-right-line text-xl"></i>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}